// Resolver schema-cache + suggestion-primitive types.
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § "Type surface (v1 read-only)".
// JSON column-type lattice: Ralph/JsonColumnSemantics/PRD.md § Locked decisions JCS-1.

import type { QualifiedName } from '../../sql-grammar/model';
import type { LooseRelationship } from '@/types/electron';

export type JsonShape = 'object' | 'array' | 'scalar' | 'unknown';

export type JsonClassificationKind =
  | 'native-jsonb'
  | 'native-json'
  | 'check-isjson'
  | 'loose-json-declared'
  | 'loose-json-inferred'
  | 'not-json';

export type JsonClassification =
  | {
      readonly kind: 'native-jsonb';
      readonly shape?: JsonShape;
      readonly pathHints?: readonly string[];
    }
  | {
      readonly kind: 'native-json';
      readonly shape?: JsonShape;
      readonly pathHints?: readonly string[];
    }
  | {
      readonly kind: 'check-isjson';
      readonly shape?: JsonShape;
      readonly pathHints?: readonly string[];
    }
  | {
      readonly kind: 'loose-json-declared';
      readonly shape?: JsonShape;
      readonly pathHints?: readonly string[];
    }
  | {
      readonly kind: 'loose-json-inferred';
      readonly shape?: JsonShape;
      readonly pathHints?: readonly string[];
    }
  | { readonly kind: 'not-json' };

// Shared singleton for the `not-json` lattice arm — the read-side default and
// the cascade fall-through value are byte-identical, so they share one const
// rather than redefining it per consumer. Value export (not `export type`).
export const NOT_JSON: JsonClassification = { kind: 'not-json' };

export type Column = {
  readonly qualified: QualifiedName;
  readonly dataType: string;
  /**
   * The type as Table Explorer spells it, when `dataType` is a bare catalog
   * name: PostgreSQL's `format_type` (`integer[]`, `character varying(10)`)
   * next to its `udt_name` (`_int4`, `varchar`). The Visual Query Editor's
   * filter chip reads it (`toTableExplorerColumn`) so it offers Table
   * Explorer's operators; everything else keeps `dataType`. Absent on the
   * other engines, whose `dataType` already carries the length / array form.
   */
  readonly formattedType?: string;
  readonly nullable: boolean;
  readonly isPrimaryKey: boolean;
  readonly isForeignKey: boolean;
  readonly foreignKeyRef?: QualifiedName;
  /**
   * Referenced TABLE's qualified name for a real FK column (reuses
   * `FkEdge.toTable`, so it carries `schema`). `foreignKeyRef` above carries
   * only the referenced COLUMN — a bare name with no schema of its own,
   * since `QualifiedName` has no dedicated table segment. Additive/optional:
   * populated by `columnsFor()` (`schemaProjectionHelpers.ts`) from the
   * per-engine `FkEdge[]` each projector already computes for real FK
   * constraints (PARITY-FK-01). Absent for non-FK columns and for any
   * caller that doesn't thread `fks` into `columnsFor()`.
   */
  readonly foreignKeyRefTable?: QualifiedName;
  /**
   * The column's real FK is its pairing inside a MULTI-column constraint
   * (`(tenant_id, customer_id) → customers(tenant_id, id)`), not a
   * single-column one. Such a pairing yields to a loose declaration on the
   * column (`realFkOutranksLoose`). Absent for single-column FKs and non-FK
   * columns.
   */
  readonly foreignKeyIsComposite?: boolean;
  readonly defaultValue?: string;
  readonly relation: QualifiedName;
  readonly jsonClassification?: JsonClassification;
  /**
   * The column's type is a NATIVE enum whose name alone doesn't say so — a
   * PostgreSQL `CREATE TYPE … AS ENUM` column reports its own type name
   * (`mood`), not `enum(…)`. Set from the introspection's `pg_enum` members
   * (`PgSchemaColumn.enumValues`) by `columnsFor()`; absent otherwise. MySQL
   * `ENUM(…)`/`SET(…)` need no flag — their `dataType` already says so.
   */
  readonly isNativeEnumType?: boolean;
};

export type TableKind = 'table' | 'view' | 'cte' | 'materialized-view' | 'synonym';

export type Table = {
  readonly qualified: QualifiedName;
  readonly kind: TableKind;
  readonly columnCount?: number;
};

export type FkDirection = 'left-references-right' | 'right-references-left';

export type JoinCandidate = {
  readonly leftTable: QualifiedName;
  readonly rightTable: Table;
  readonly fkEdge: {
    readonly name?: string;
    readonly leftColumns: readonly string[];
    readonly rightColumns: readonly string[];
    readonly direction: FkDirection;
    /**
     * Pre-formatted, fully alias-qualified ON predicate. Set ONLY by loose
     * rels that need more than a column equality: JSON-path rels
     * (`sourcePath`), where the source side is an engine-specific JSON-extract
     * expression with the alias placed *inside* the extract (e.g.
     * `t.id = o.metadata #>> '{customer_id}'`), and rels with a
     * `sourceFilter` / `targetFilter`, whose filters are ANDed on as
     * parenthesised, per-side-qualified predicates — so the shared
     * `buildJoinSnippet` / `formatJoinOnText` must emit it verbatim instead of
     * re-prefixing an alias onto the bare column. The new relation is
     * qualified by `suggestedAlias` (no placeholder — a filter literal may
     * read like one); `snippet` carries the `${1}` tab-stop form. Absent for
     * real FKs and plain unfiltered loose rels.
     */
    readonly onPredicate?: string;
  };
  readonly suggestedAlias: string;
  readonly snippet: string; // Monaco-compatible body
  /**
   * Set only on candidates built from a user-declared loose relationship
   * (`SchemaSnapshot.looseRelationships`) — never on a real FK constraint, so
   * the JOIN list can badge it as such. `sourceSide` names which side of the
   * join holds the relationship's source (from) table; its `sourceFilter` /
   * `targetFilter` are already part of `fkEdge.onPredicate`. Additive.
   */
  readonly loose?: {
    readonly id: string;
    readonly sourceSide: 'left' | 'right';
    readonly sourceFilter?: LooseRelationship['sourceFilter'];
    readonly targetFilter?: LooseRelationship['targetFilter'];
  };
};

export type RoutineParamMode = 'in' | 'out' | 'inout' | 'return';

export type RoutineParam = {
  readonly name?: string;
  readonly dataType: string;
  readonly mode: RoutineParamMode;
  readonly hasDefault: boolean;
};

export type RoutineKind = 'scalar-function' | 'table-function' | 'aggregate' | 'window' | 'procedure';

export type RoutineSignature = {
  readonly qualified: QualifiedName;
  readonly kind: RoutineKind;
  readonly params: readonly RoutineParam[];
  readonly returnType?: string;
};

export type Routine = {
  readonly signature: RoutineSignature;
  readonly overloads?: readonly RoutineSignature[];
};

export type FkEdge = {
  readonly fromTable: QualifiedName;
  readonly fromColumns: readonly string[];
  readonly toTable: QualifiedName;
  readonly toColumns: readonly string[];
  readonly name?: string;
};

export type SchemaSnapshot = {
  readonly engineId: string;
  readonly connectionId: string;
  readonly database?: string;
  /**
   * Keyed by `relationKey(qualified)` (`service/snapshotRelations.ts`):
   * `schema.name`, folded, when the relation has a schema — so same-named
   * relations in two schemas stay two entries (injective: a dotted delimited
   * name is quoted in the key). Resolve a reference taken from SQL text with
   * `lookupSnapshotRelation` / `lookupRelationColumns`, never by indexing
   * these maps with a folded name.
   */
  readonly tables: ReadonlyMap<string, Table>;
  readonly columns: ReadonlyMap<string, readonly Column[]>; // same key as `tables`
  /**
   * Folded schemas an UNQUALIFIED relation reference resolves through, in
   * order: PostgreSQL's search_path, SQL Server's default schema of the
   * database user then `dbo`, the Oracle connected schema, the MySQL
   * database. Absent for schemaless engines (SQLite).
   */
  readonly defaultSchemas?: readonly string[];
  /**
   * `defaultSchemas` is the session's own resolution path, read from the
   * server (`current_setting('search_path')`, `SCHEMA_NAME()`, the connected
   * schema / database): an unqualified reference then resolves ONLY through
   * it, as the engine resolves it. Absent when the list is a fallback guess
   * (the probe failed) — a bare name then also resolves to the only relation
   * of that name (`lookupSnapshotRelation`).
   */
  readonly defaultSchemasExact?: boolean;
  readonly routines: ReadonlyMap<string, readonly Routine[]>;
  readonly foreignKeys: readonly FkEdge[];
  /**
   * User-declared loose foreign keys from `DatabaseMetaInfo.looseRelationships`
   * — logical references the schema itself doesn't carry. Includes JSON-path
   * loose FKs (`sourcePath` set). Additive/optional: absent on snapshots built
   * without the MetaInfo lookup (pre-Phase-5 behavior). Consumed by
   * `suggestJoinCandidates` to surface loose FKs as JOIN candidates like real
   * FK constraints.
   */
  readonly looseRelationships?: readonly LooseRelationship[];
  readonly fetchedAt: number;
  /**
   * Identity of this snapshot's CONTENT, for caches keyed on "which snapshot".
   * `fetchedAt` is the native schema cache's build time, and a MetaInfo-only
   * change (a loose FK or JSON declaration folded in at pull time) does not
   * move it — so `buildPullSchemaSnapshot` stamps `fetchedAt` plus a hash of
   * the folded-in MetaInfo here. Absent on hand-built snapshots; readers fall
   * back to `fetchedAt` (`snapshotCacheIdentity` in the VQE suggestion hooks).
   */
  readonly revision?: string;
};
